import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Clock, X } from 'lucide-react';
import { haptic } from '../utils/vibration';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';
import FloatingField, { normalizeFloatingLabel } from './FloatingField';
import ModalShell from './ModalShell';
const TimeSlotPicker = ({
  value,
  onChange,
  label,
  placeholder = "Выберите время",
  disabled = false,
  disabledBeforeMinutes = null,
  inputClassName = '',
  options = null,
  startHour = 7,
  endHour = 20,
  slotHours = 1,
  modalTitle = 'Выберите время'
}) => {
  const { animationsEnabled } = useInteractionPreferences();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(value || '');
  const lastHapticSlotRef = useRef(value || '');
  const scrollRef = useRef(null);
  const itemHeight = 48; // px — высота одного элемента

  useEffect(() => {
    setSelectedTimeSlot(value || '');
    lastHapticSlotRef.current = value || '';
  }, [value]);

  const timeSlots = useMemo(() => {
    if (Array.isArray(options) && options.length > 0) {
      return options.map((option) => String(option));
    }

    const generatedSlots = [];
    for (let hour = startHour; hour + slotHours <= endHour; hour += slotHours) {
      const startTime = `${hour.toString().padStart(2, '0')}:00`;
      const endTime = `${(hour + slotHours).toString().padStart(2, '0')}:00`;
      generatedSlots.push(`${startTime}-${endTime}`);
    }
    return generatedSlots;
  }, [endHour, options, slotHours, startHour]);

  const getSlotStartMinutes = (timeSlot) => {
    const [hours, minutes] = String(timeSlot).split('-')[0].split(':').map(Number);
    return hours * 60 + minutes;
  };

  const isTimeSlotDisabled = (timeSlot) => (
    disabledBeforeMinutes !== null && getSlotStartMinutes(timeSlot) <= disabledBeforeMinutes
  );

  const nearestEnabledTimeSlot = (targetIdx) => {
    for (let idx = targetIdx; idx < timeSlots.length; idx++) {
      if (!isTimeSlotDisabled(timeSlots[idx])) return { slot: timeSlots[idx], idx };
    }
    for (let idx = targetIdx - 1; idx >= 0; idx--) {
      if (!isTimeSlotDisabled(timeSlots[idx])) return { slot: timeSlots[idx], idx };
    }
    return null;
  };

  useEffect(() => {
    if (selectedTimeSlot && isTimeSlotDisabled(selectedTimeSlot)) {
      setSelectedTimeSlot('');
      onChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabledBeforeMinutes, selectedTimeSlot]);

  // Прокрутить к выбранному элементу при открытии
  useEffect(() => {
    if (isOpen && selectedTimeSlot && scrollRef.current) {
      const idx = timeSlots.indexOf(selectedTimeSlot);
      if (idx >= 0) {
        // Даём DOM обновиться затем скроллим
        requestAnimationFrame(() => {
          const container = scrollRef.current;
          if (container) {
            container.scrollTo({
              top: idx * itemHeight,
              behavior: 'auto'
            });
          }
        });
      }
    }
  }, [isOpen, selectedTimeSlot, timeSlots]);

  // При остановке скролла — snap к ближайшему элементу
  const handleScrollEnd = () => {
    if (!scrollRef.current) return;
    snapToNearest();
  };

  // Snap к ближайшему элементу
  const snapToNearest = () => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const scrollTop = container.scrollTop;
    const idx = Math.round(scrollTop / itemHeight);
    const clampedIdx = Math.max(0, Math.min(timeSlots.length - 1, idx));

    container.scrollTo({
      top: clampedIdx * itemHeight,
      behavior: animationsEnabled ? 'smooth' : 'auto'
    });

    const enabled = nearestEnabledTimeSlot(clampedIdx);
    if (!enabled) return;

    const newSlot = enabled.slot;
    if (lastHapticSlotRef.current !== newSlot) {
      haptic('tap');
      lastHapticSlotRef.current = newSlot;
    }
    setSelectedTimeSlot(newSlot);
    onChange(newSlot);
  };

  // Fallback: проверяем scroll при обычной прокрутке
  const handleScroll = () => {
    // Debounce
    clearTimeout(scrollRef.scrollTimeout);
    scrollRef.scrollTimeout = setTimeout(snapToNearest, 150);
  };

  const clearSelection = () => {
    setSelectedTimeSlot('');
    onChange('');
  };

  const formatDisplayTimeSlot = (timeSlot) => {
    if (!timeSlot) return '';
    return String(timeSlot).replace('-', '–');
  };

  // Cleanup timeout при размонтировании
  useEffect(() => {
    return () => {
      if (scrollRef.scrollTimeout) {
        clearTimeout(scrollRef.scrollTimeout);
      }
    };
  }, []);

  // Сколько пустых элементов добавить сверху и снизу для центрирования
  const visibleCount = 5; // количество видимых элементов
  const paddingCount = Math.floor(visibleCount / 2);
  const paddingHeight = paddingCount * itemHeight;

  return (
    <div className="relative">
      {/* Поле ввода */}
      <FloatingField label={label || normalizeFloatingLabel(placeholder, 'Время')} labelOffset="1.5rem" filled={!!selectedTimeSlot} active={isOpen} disabled={disabled}>
        <div className="relative">
          <input
            type="text"
            value={formatDisplayTimeSlot(selectedTimeSlot)}
            onChange={() => {}} // Read-only
            onClick={() => {
              if (!disabled) {
                haptic('open');
                setIsOpen(true);
              }
            }}
            onFocus={(e) => e.target.blur()}
            inputMode="none"
            tabIndex={-1}
            className={`w-full px-2 py-2 border rounded-xl text-[10px] tabular-nums tracking-[-0.02em] placeholder:text-[11px] placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-colors dark:bg-gray-900 dark:text-gray-100 appearance-none cursor-pointer ${
              selectedTimeSlot
                ? 'border-green-500 focus:ring-green-500'
                : 'border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-500'
            } ${selectedTimeSlot ? 'text-center pl-7 pr-7' : 'text-left pl-6 pr-7'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${inputClassName}`}
            placeholder={placeholder}
            readOnly
          />
          <Clock className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-blue-500 pointer-events-none" />
          {selectedTimeSlot && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                haptic('close');
                clearSelection();
              }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </FloatingField>

      {/* Модальное окно */}
      <ModalShell
        open={isOpen}
        onClose={() => setIsOpen(false)}
        variant="window"
        zIndexClass="z-[9999]"
        panelClassName="max-w-sm w-full rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden !p-0"
      >
                {/* Заголовок */}
                <div className="flex items-center justify-between px-6 pt-5 pb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {modalTitle}
                  </h3>
                  <button
                    onClick={() => {
                      haptic('close');
                      setIsOpen(false);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                {/* Picker с прокруткой */}
                <div className="relative px-6 pb-5">
                  {/* Градиенты затухания */}
                  <div
                    className="absolute left-6 right-6 top-0 z-10 pointer-events-none bg-gradient-to-b from-white dark:from-gray-900 to-transparent"
                    style={{
                      height: `${paddingCount * itemHeight}px`,
                    }}
                  />
                  <div
                    className="absolute left-6 right-6 bottom-0 z-10 pointer-events-none bg-gradient-to-t from-white dark:from-gray-900 to-transparent"
                    style={{
                      height: `${paddingCount * itemHeight}px`,
                    }}
                  />

                  {/* Центральная линия */}
                  <div
                    className="absolute left-6 right-6 z-5 pointer-events-none rounded-lg border-2 border-blue-500"
                    style={{
                      top: `${paddingCount * itemHeight}px`,
                      height: `${itemHeight}px`
                    }}
                  />

                  {/* Контейнер прокрутки */}
                  <div
                    ref={scrollRef}
                    className="overflow-y-auto overscroll-contain scrollbar-hide"
                    style={{
                      height: `${visibleCount * itemHeight}px`,
                      paddingTop: `${paddingHeight}px`,
                      paddingBottom: `${paddingHeight}px`,
                      scrollSnapType: 'y mandatory',
                      scrollBehavior: 'smooth',
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}
                    onScroll={handleScroll}
                    onScrollEnd={handleScrollEnd}
                  >
                    {/* Элементы */}
                    {timeSlots.map((timeSlot) => {
                      const timeSlotDisabled = isTimeSlotDisabled(timeSlot);
                      return (
                      <div
                        key={timeSlot}
                        onClick={() => {
                          if (timeSlotDisabled) return;
                          haptic('tap');
                          lastHapticSlotRef.current = timeSlot;
                          setSelectedTimeSlot(timeSlot);
                          onChange(timeSlot);
                          setIsOpen(false);
                        }}
                        className={`text-center select-none transition-all flex items-center justify-center ${
                          timeSlotDisabled
                            ? 'cursor-not-allowed text-gray-300 dark:text-gray-700'
                            : 'cursor-pointer'
                        } ${
                          selectedTimeSlot === timeSlot
                            ? 'text-blue-600 dark:text-blue-400 font-bold text-lg'
                            : timeSlotDisabled
                              ? 'text-sm'
                              : 'text-gray-600 dark:text-gray-400 text-base'
                        }`}
                        style={{
                          height: `${itemHeight}px`,
                          scrollSnapAlign: 'center',
                          scrollSnapStop: 'always'
                        }}
                      >
                        {timeSlot}
                      </div>
                      );
                    })}
                  </div>
                </div>
      </ModalShell>
    </div>
  );
};

export default TimeSlotPicker;
